import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from 'react';
import type { DealerSearch } from '@/app/components/sections/dealer/utils/types';
import { cn } from '@/app/utils/cn';
import type { DealerGrouping } from '@/services/contentstack/models/dealer';
interface InfiniteScrollListProps {
  className?: string;
  items: DealerSearch[] | DealerGrouping[];
  itemsPerLoad?: number;
  renderItem: (item: DealerSearch | DealerGrouping, index: number) => ReactNode;
}
export const InfiniteScrollList = ({
  className,
  items = [],
  itemsPerLoad = 10,
  renderItem
}: InfiniteScrollListProps) => {
  const [visibleCount, setVisibleCount] = useState<number>(itemsPerLoad);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const resultItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;
  const onIntersect = useEffectEvent((entries: IntersectionObserverEntry[]) => {
    if (entries.length > 0 && entries[0].isIntersecting && hasMore) {
      setVisibleCount(prev => Math.min(prev + itemsPerLoad, items.length));
    }
  });
  useEffect(() => {
    setVisibleCount(itemsPerLoad);
  }, [items, itemsPerLoad]);
  useEffect(() => {
    const node = lastItemRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(entries_0 => {
      onIntersect(entries_0);
    }, {
      threshold: 0.1,
      rootMargin: '150px'
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [resultItems.length, hasMore]);
  return resultItems.map((item, index) => {
    const isLastItem = index === resultItems.length - 1;
    return <div className={cn({
      [className as string]: !!className,
      'pb-4': isLastItem
    })} key={index} ref={isLastItem ? lastItemRef : null} style={{
      containIntrinsicSize: 'auto 100px',
      contentVisibility: 'auto'
    }}>
        {renderItem(item, index)}
      </div>;
  });
};